import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { PHIN_GREETING } from './strings';
import { createPortal } from 'react-dom';
import { Button } from '@belong/ui';
import css from './AiPointer.module.css';
import { explainPointerTarget } from './explain';
import type { PointerAction, PointerExplanation, PointerTarget } from './types';
import {
  POINTER_BUTTON_ATTRIBUTE,
  POINTER_CLOSED_EVENT,
  POINTER_POPOVER_ATTRIBUTE,
} from './open-event';
import { AVATAR_SIZE, POPOVER_WIDTH } from './constants';
import { MOBILE_BREAKPOINT } from './media';
import { PhinAvatarLazy } from './PhinAvatarLazy';
import { TypedText } from './TypedText';
import { usePointerOpen } from './use-pointer-open';

const PEEK_PADDING = 20;
const PEEK_LABEL_DELAY_MS = 224;
const PEEK_LABEL_GAP = 10;
const PEEK_LABEL = 'Need a help?';
const PEEK_TYPE_MS = PEEK_LABEL.length * 32;
const CURSOR_OFFSET = 16;
const VIEWPORT_GAP = 12;
const EXIT_MS = 200;
const DISMISS_GRACE_MS = 300;
const FAST_SCROLL_PX_PER_MS = 2;

type Position = { top: number; left: number };

function getPosition(
  x: number,
  y: number,
  width: number,
  height: number,
  align: 'start' | 'end' | 'flush' = 'start',
): Position {
  const maxLeft = window.innerWidth - width - VIEWPORT_GAP;
  const right = x + CURSOR_OFFSET;
  const flushEnd = Math.max(VIEWPORT_GAP, Math.min(x - width, maxLeft));
  const left =
    align === 'end'
      ? flushEnd
      : align === 'flush'
        ? Math.max(VIEWPORT_GAP, Math.min(x, maxLeft))
        : right > maxLeft
          ? Math.max(VIEWPORT_GAP, x - width - CURSOR_OFFSET)
          : right;
  const maxTop = window.innerHeight - height - VIEWPORT_GAP;
  const top = Math.max(VIEWPORT_GAP, Math.min(y + CURSOR_OFFSET, maxTop));
  return { top, left };
}

function focusTarget(focus: string) {
  const scope =
    focus === '@empty'
      ? Array.from(
          document.querySelectorAll<HTMLInputElement>('input, textarea'),
        ).find((input) => input.type !== 'hidden' && input.value === '')
      : document.querySelector<HTMLElement>(
          `[data-pointer="${focus}"] input, [data-pointer="${focus}"] button, [data-pointer="${focus}"]`,
        );
  scope?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  scope?.focus({ preventScroll: true });
}

function ActionLink({
  step,
  onDone,
}: {
  step: PointerAction;
  onDone: () => void;
}) {
  if (step.href) {
    return (
      <a href={step.href} className={css.step}>
        {step.label}
        <span aria-hidden>→</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      className={css.step}
      onClick={() => {
        onDone();
        if (step.focus) focusTarget(step.focus);
      }}
    >
      {step.label}
      <span aria-hidden>→</span>
    </button>
  );
}

export function Answer({
  explanation,
  onDismiss,
  showDismiss = false,
  onComplete,
}: {
  explanation: PointerExplanation;
  onDismiss: () => void;
  showDismiss?: boolean;
  onComplete?: () => void;
}) {
  const [activeLine, setActiveLine] = useState(0);
  const hasExample = Boolean(explanation.example);
  const stepsLine = hasExample ? 3 : 2;
  const advance = (line: number) => () =>
    setActiveLine((current) => Math.max(current, line + 1));

  const isComplete = activeLine >= stepsLine;
  useEffect(() => {
    if (isComplete) onComplete?.();
  }, [isComplete, onComplete]);

  return (
    <>
      <p className={css.title}>
        <TypedText
          text={explanation.title}
          reserve={false}
          onDone={advance(0)}
        />
      </p>
      {activeLine >= 1 && (
        <p className={css.summary}>
          <TypedText
            text={explanation.summary}
            reserve={false}
            onDone={advance(1)}
          />
        </p>
      )}
      {explanation.example && activeLine >= 2 && (
        <div className={css.example}>
          <p className={css.exampleLabel}>Example</p>
          <p className={css.exampleText}>
            <TypedText
              text={explanation.example}
              reserve={false}
              onDone={advance(2)}
            />
          </p>
        </div>
      )}
      {showDismiss && activeLine >= stepsLine && (
        <div className={css.dismissRow}>
          <Button variant="text" buttonType="secondary" onClick={onDismiss}>
            Got It
          </Button>
        </div>
      )}
      {activeLine >= stepsLine && explanation.nextSteps.length > 0 && (
        <ul className={css.steps}>
          {explanation.nextSteps.map((step, index) => (
            <li
              key={step.label}
              className={css.stepItem}
              style={{ animationDelay: `${index * 90}ms` }}
            >
              <ActionLink step={step} onDone={onDismiss} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function getAvatarState(
  isOpen: boolean,
  hasExplanation: boolean,
  isSleeping?: boolean,
) {
  if (isSleeping) return 'sleeping';
  return isOpen && !hasExplanation ? 'working' : 'default';
}

function PeekLabel({
  onWidth,
  isSleeping,
}: {
  onWidth: (width: number) => void;
  isSleeping?: boolean;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    setTextWidth(textRef.current?.offsetWidth ?? 0);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsExpanded(true), PEEK_LABEL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const width = isExpanded && !isSleeping ? textWidth + PEEK_LABEL_GAP : 0;

  useLayoutEffect(() => onWidth(width), [width, onWidth]);

  return (
    <p
      className={css.peekLabel}
      style={{
        width,
        transitionDuration: `${PEEK_TYPE_MS}ms`,
        transitionTimingFunction: `steps(${PEEK_LABEL.length}, end)`,
      }}
      aria-label={PEEK_LABEL}
    >
      <span ref={textRef} aria-hidden>
        {PEEK_LABEL}
      </span>
    </p>
  );
}

function PointerPopover({
  target,
  isClosing,
  onDismiss,
  onExpand,
  onHold,
}: {
  target: PointerTarget;
  isClosing: boolean;
  onDismiss: () => void;
  onExpand: () => void;
  onHold: (hovering: boolean) => void;
}) {
  const isOpen = target.mode === 'open';
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [peekLabelWidth, setPeekLabelWidth] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [scrollShift, setScrollShift] = useState({ x: 0, y: 0 });
  const markComplete = useCallback(() => setIsComplete(true), []);
  const [height, setHeight] = useState<number | null>(null);
  const [explanation, setExplanation] = useState<PointerExplanation | null>(
    null,
  );

  useEffect(() => {
    setExplanation(null);
    setIsComplete(false);
    if (!isOpen) return undefined;
    const controller = new AbortController();
    explainPointerTarget(target.detail, target.help, controller.signal)
      .then(setExplanation)
      .catch(() => undefined);
    return () => controller.abort();
  }, [target, isOpen]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;
    const measure = () => {
      setHeight(content.offsetHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isOpen && !isComplete) return undefined;
    const armedAt = Date.now() + DISMISS_GRACE_MS;
    const onPointerDown = (event: PointerEvent) => {
      if (Date.now() < armedAt) return;
      const node = event.target as Element;
      if (ref.current?.contains(node)) return;
      if (node.closest(`[${POINTER_BUTTON_ATTRIBUTE}]`)) return;
      onDismiss();
    };
    document.addEventListener('pointerdown', onPointerDown);
    const onScrollDismiss = (event: Event) => {
      if (Date.now() < armedAt) return;
      if (event.target === document) onDismiss();
    };
    window.addEventListener('scroll', onScrollDismiss, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScrollDismiss, true);
    };
  }, [onDismiss, isOpen, isComplete]);

  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(() => {
    const base = { x: window.scrollX, y: window.scrollY };
    let last = { y: window.scrollY, at: performance.now() };
    let frame = 0;
    const apply = () => {
      frame = 0;
      setScrollShift({
        x: base.x - window.scrollX,
        y: base.y - window.scrollY,
      });
    };
    const onScroll = (event: Event) => {
      if (event.target !== document) return;
      const now = performance.now();
      const speed =
        Math.abs(window.scrollY - last.y) / Math.max(now - last.at, 1);
      last = { y: window.scrollY, at: now };
      if (speed > FAST_SCROLL_PX_PER_MS) {
        onDismissRef.current();
        return;
      }
      if (!frame) frame = requestAnimationFrame(apply);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || target.isAutomatic) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus({ preventScroll: true });
    return () => previous?.focus?.({ preventScroll: true });
  }, [isOpen, target.isAutomatic]);

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;
  const openWidth = isMobile ? viewportWidth - VIEWPORT_GAP * 2 : POPOVER_WIDTH;

  const width = isOpen
    ? openWidth
    : AVATAR_SIZE + peekLabelWidth + PEEK_PADDING;
  const position = getPosition(
    isOpen && isMobile ? VIEWPORT_GAP : target.x,
    target.y,
    width,
    height ?? 0,
    isOpen && isMobile ? 'flush' : target.align,
  );

  return (
    <div
      ref={ref}
      role={isOpen ? 'dialog' : undefined}
      aria-label={isOpen ? 'Phin help' : undefined}
      tabIndex={isOpen ? -1 : undefined}
      {...{ [POINTER_POPOVER_ATTRIBUTE]: '' }}
      data-mode={target.mode}
      onMouseEnter={() => onHold(true)}
      onMouseLeave={() => onHold(false)}
      className={isClosing ? `${css.popover} ${css.closing}` : css.popover}
      style={{
        top: position.top + scrollShift.y,
        left: position.left + scrollShift.x,
        width,
        ['--peek-ms' as string]: `${PEEK_TYPE_MS}ms`,
        height: height ?? 'auto',
        transformOrigin: `${target.originX - position.left}px ${target.originY - position.top}px`,
        visibility: height === null ? 'hidden' : 'visible',
      }}
    >
      <div
        className={css.box}
        onClick={isOpen ? undefined : onExpand}
        onKeyDown={
          isOpen
            ? undefined
            : (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onExpand();
                }
              }
        }
        tabIndex={isOpen ? undefined : 0}
        role={isOpen ? undefined : 'button'}
      >
        {isOpen && (
          <div className={css.beam} data-thinking={!explanation} aria-hidden>
            <span className={css.beamBase} />
            {[0, 1, 2, 3, 4].map((index) => (
              <span
                key={index}
                className={css.beamWave}
                style={{ '--i': index } as CSSProperties}
              />
            ))}
            <span className={css.beamCore} />
          </div>
        )}
        <div
          ref={contentRef}
          className={css.content}
          style={isOpen ? { width: openWidth } : undefined}
        >
          <div className={css.header}>
            <div
              className={css.avatar}
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
            >
              <PhinAvatarLazy
                size={AVATAR_SIZE}
                state={getAvatarState(
                  isOpen,
                  Boolean(explanation),
                  target.sleeping,
                )}
              />
            </div>
            {isOpen ? (
              <p className={css.name}>{PHIN_GREETING}</p>
            ) : (
              <PeekLabel
                onWidth={setPeekLabelWidth}
                isSleeping={target.sleeping}
              />
            )}
          </div>
          {isOpen &&
            (explanation ? (
              <Answer
                explanation={explanation}
                onDismiss={onDismiss}
                showDismiss
                onComplete={markComplete}
              />
            ) : (
              <div className={css.thinking} aria-busy="true">
                <span />
                <span />
                <span />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export function AiPointer({ errorMessage }: { errorMessage?: string }) {
  const { target, dismiss, expand, hold } = usePointerOpen(errorMessage);
  const [rendered, setRendered] = useState<PointerTarget | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (target?.mode === 'open') {
      wasOpen.current = true;
      return;
    }
    if (!target && wasOpen.current) {
      wasOpen.current = false;
      window.dispatchEvent(new Event(POINTER_CLOSED_EVENT));
    }
  }, [target]);

  useEffect(() => {
    if (target) {
      setRendered(target);
      return undefined;
    }
    const timer = setTimeout(() => setRendered(null), EXIT_MS);
    return () => clearTimeout(timer);
  }, [target]);

  if (!rendered) return null;
  return createPortal(
    <PointerPopover
      target={rendered}
      isClosing={!target}
      onDismiss={dismiss}
      onExpand={expand}
      onHold={hold}
    />,
    document.body,
  );
}
